import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

const T = {
  bg:        '#060d1e',
  surface:   '#0d1a2d',
  surfaceHi: '#132238',
  border:    '#1e3a5f',
  borderHi:  '#2d5a8e',
  text:      '#f0f6ff',
  textSub:   '#8ba5c8',
  textMute:  '#4a6a8a',
  emerald:   '#10b981',
  emeraldDim:'rgba(16,185,129,0.10)',
  blue:      '#3b82f6',
  blueDim:   'rgba(59,130,246,0.10)',
  green:     '#22c55e',
  amber:     '#f59e0b',
  red:       '#ef4444',
  purple:    '#a78bfa',
  teal:      '#2dd4bf',
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatPostmortem = (text, isMobile) => {
  if (!text) return null;
  const sections = [
    { key: 'WHAT HAPPENED',     color: T.textSub },
    { key: 'WHY IT FAILED',     color: T.red     },
    { key: 'STOCK IMPACT',      color: T.amber   },
    { key: 'CAN THEY RECOVER',  color: T.blue    },
    { key: 'INVESTOR TAKEAWAY', color: T.emerald },
  ];
  const parts = {};
  sections.forEach(({ key }) => {
    const regex = new RegExp(`${key}:\\s*(.+?)(?=${sections.map(s => s.key).join('|')}:|$)`, 's');
    const match = text.match(regex);
    if (match) parts[key] = match[1].trim();
  });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {sections.map(({ key, color }) =>
        parts[key] ? (
          <div key={key} style={{
            display: 'flex', gap: 12, alignItems: 'flex-start',
            padding: isMobile ? '12px 14px' : '14px 18px',
            backgroundColor: `${color}08`, borderRadius: 10, border: `1px solid ${color}20`,
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 5 }}>{key}</div>
              <span style={{ color: '#d8e8ff', fontSize: isMobile ? 13 : 14, lineHeight: 1.75 }}>{parts[key]}</span>
            </div>
          </div>
        ) : null
      )}
    </div>
  );
};

export default function FDAFailures({ session, plan }) {
  const [rejections, setRejections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < 768;

  useEffect(() => {
    fetchRejections();
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  async function fetchRejections() {
    setLoading(true);
    const { data } = await supabase.from('fda_rejections').select('*').order('rejection_date', { ascending: false });
    setRejections(data || []);
    setLoading(false);
  }

  const filtered = rejections.filter(r => {
    if (filter === 'resubmit'    && !r.can_resubmit) return false;
    if (filter === 'no_resubmit' &&  r.can_resubmit) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (r.drug_name?.toLowerCase().includes(q) || r.company?.toLowerCase().includes(q) || r.indication?.toLowerCase().includes(q) || r.ticker?.toLowerCase().includes(q));
    }
    return true;
  });

  const canResubmitCount = rejections.filter(r => r.can_resubmit).length;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: T.text, margin: '0 0 6px', letterSpacing: '-0.5px' }}>
          FDA Failure Post-Mortems
        </h2>
        <p style={{ color: T.textSub, fontSize: isMobile ? 14 : 16, margin: 0, lineHeight: 1.6 }}>
          AI-powered analysis of FDA rejections — understand why drugs failed and what it means for investors.
        </p>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 10, marginBottom: 24 }}>
        {[
          { label: 'Total Rejections', value: rejections.length,                             color: T.red     },
          { label: 'Can Resubmit',     value: canResubmitCount,                               color: T.blue    },
          { label: 'Likely Dead',      value: rejections.length - canResubmitCount,           color: T.textSub },
          { label: 'AI Analyses',      value: rejections.filter(r => r.ai_postmortem).length, color: T.emerald },
        ].map((s, i) => (
          <div key={i} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: isMobile ? '14px 16px' : '18px 22px' }}>
            <div style={{ fontSize: 11, color: T.textMute, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{s.label}</div>
            <div style={{ fontSize: isMobile ? 26 : 32, fontWeight: 800, color: s.color, lineHeight: 1 }}>{loading ? '—' : s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ width: '100%', position: 'relative' }}>
          <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: T.textMute, fontSize: 15 }}>⌕</span>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search drug, company, indication..."
            style={{ width: '100%', padding: '11px 14px 11px 38px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, color: T.text, fontSize: 15, outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
            onFocus={e => e.target.style.borderColor = T.red}
            onBlur={e => e.target.style.borderColor = T.border} />
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { id: 'all',         label: `All (${rejections.length})`,                                              color: T.red     },
            { id: 'resubmit',    label: isMobile ? `Can Resubmit (${canResubmitCount})` : `Can Resubmit (${canResubmitCount})`,   color: T.blue    },
            { id: 'no_resubmit', label: `Likely Dead (${rejections.length - canResubmitCount})`,                   color: T.textSub },
          ].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)} style={{
              padding: isMobile ? '8px 14px' : '10px 18px', borderRadius: 8, cursor: 'pointer',
              fontSize: isMobile ? 12 : 14, fontWeight: 600,
              border: `1px solid ${filter === f.id ? f.color : T.border}`,
              background: filter === f.id ? `${f.color}18` : 'transparent',
              color: filter === f.id ? f.color : T.textSub, transition: 'all 0.15s',
            }}>{f.label}</button>
          ))}
        </div>
      </div>

      <div style={{ fontSize: 14, color: T.textMute, marginBottom: 16 }}>
        {filtered.length} results · Click any card to see AI post-mortem
      </div>

      {/* Cards */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: T.textMute }}>
          <div style={{ fontSize: 15, marginBottom: 12 }}>Loading FDA rejections...</div>
          <div style={{ width: 80, height: 2, background: T.border, borderRadius: 1, overflow: 'hidden', margin: '0 auto' }}>
            <div style={{ height: '100%', width: '50%', background: T.emerald, animation: 'loadBar 1.4s ease-in-out infinite' }} />
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: T.textMute }}>
          <div style={{ fontSize: 16, marginBottom: 8 }}>No rejections found</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((r, i) => {
            const isExpanded = expanded === i;
            return (
              <div key={i} style={{
                background: T.surface, borderRadius: 14,
                border: `1px solid ${isExpanded ? `${T.red}40` : T.border}`,
                borderLeft: `3px solid ${T.red}`,
                overflow: 'hidden', cursor: 'pointer', transition: 'all 0.2s',
              }}
                onClick={() => setExpanded(isExpanded ? null : i)}
                onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.borderColor = T.borderHi; }}
                onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.borderColor = T.border; }}
              >
                {/* Card Header */}
                <div style={{ padding: isMobile ? '16px' : '20px 22px', display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 16 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                      <h3 style={{ margin: 0, color: T.text, fontSize: isMobile ? 15 : 17, fontWeight: 700 }}>{r.drug_name}</h3>
                      {r.ticker && <span style={{ fontSize: 11, fontWeight: 700, color: T.emerald, background: T.emeraldDim, padding: '2px 9px', borderRadius: 100 }}>{r.ticker}</span>}
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 100, background: r.can_resubmit ? T.blueDim : 'rgba(74,106,138,0.15)', color: r.can_resubmit ? T.blue : T.textSub }}>
                        {r.can_resubmit ? 'Can Resubmit' : 'Likely Dead'}
                      </span>
                    </div>
                    <div style={{ fontSize: 14, color: T.textSub, fontWeight: 500 }}>{r.company}</div>
                  </div>
                  <div style={{ flexShrink: 0, textAlign: 'right' }}>
                    <div style={{ fontSize: 13, color: T.textSub, marginBottom: 5 }}>{fmtDate(r.rejection_date)}</div>
                    <span style={{ fontSize: 11, color: T.red, fontWeight: 700, background: 'rgba(239,68,68,0.1)', padding: '3px 10px', borderRadius: 100 }}>CRL</span>
                  </div>
                </div>

                {r.reason_summary && (
                  <div style={{ padding: `0 ${isMobile ? 16 : 22}px 16px`, fontSize: isMobile ? 13 : 14, color: T.textSub, lineHeight: 1.7 }}>
                    {r.reason_summary}
                  </div>
                )}

                {/* Expanded AI Post-Mortem */}
                {isExpanded && r.ai_postmortem && (
                  <div style={{ borderTop: `1px solid ${T.border}`, padding: isMobile ? 16 : 22 }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                      <div style={{ width: 26, height: 26, background: `linear-gradient(135deg,${T.red},#f97316)`, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>✦</div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: T.red, letterSpacing: '0.8px' }}>AI POST-MORTEM</span>
                    </div>
                    {formatPostmortem(r.ai_postmortem, isMobile)}
                    {r.resubmission_notes && (
                      <div style={{ marginTop: 14, padding: '12px 16px', background: T.blueDim, border: `1px solid rgba(59,130,246,0.2)`, borderRadius: 10 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: T.blue }}>Resubmission Path: </span>
                        <span style={{ fontSize: 14, color: T.textSub }}>{r.resubmission_notes}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 28, padding: '14px 18px', background: 'rgba(239,68,68,0.04)', border: `1px solid rgba(239,68,68,0.15)`, borderRadius: 10 }}>
        <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.7 }}>
          <strong style={{ color: T.text }}>Source:</strong> FDA Complete Response Letters and public records. AI analysis by Claude. Not financial advice.
        </div>
      </div>

      <style>{`@keyframes loadBar { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }`}</style>
    </div>
  );
}